import { Controller } from "@hotwired/stimulus"

/**
 * Single-layer parallax: background moves with pointer (desktop) and device tilt (mobile/iOS).
 * On iOS, device orientation is requested on first touch of the hero (required by the browser).
 */
export default class extends Controller {
  static targets = ["bg"]
  static values = {
    imageUrl: String,
    maxShift: { type: Number, default: 18 },
    smoothing: { type: Number, default: 0.08 }
  }

  connect() {
    if (this.hasBgTarget && this.imageUrlValue) {
      this.bgTarget.style.backgroundImage = `url("${this.imageUrlValue}")`
    }

    this.targetX = 0
    this.targetY = 0
    this.curX = 0
    this.curY = 0

    this.onPointerMove = this.onPointerMove.bind(this)
    this.animate = this.animate.bind(this)

    window.addEventListener("pointermove", this.onPointerMove, { passive: true })

    // iOS requires permission from a user gesture; request on first touch/click.
    // Use non-passive so the gesture is properly recognized; add click as fallback.
    if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
      this.boundRequestTilt = this.requestTiltOnTouch.bind(this)
      this.element.addEventListener("touchstart", this.boundRequestTilt, { once: true })
      this.element.addEventListener("click", this.boundRequestTilt, { once: true })
    } else if (typeof DeviceOrientationEvent !== "undefined") {
      this.addTiltListener()
    }

    this.raf = requestAnimationFrame(this.animate)
  }

  disconnect() {
    window.removeEventListener("pointermove", this.onPointerMove)
    if (this.boundRequestTilt) {
      this.element.removeEventListener("touchstart", this.boundRequestTilt)
      this.element.removeEventListener("click", this.boundRequestTilt)
    }
    if (this.onDeviceOrientation) {
      window.removeEventListener("deviceorientation", this.onDeviceOrientation)
    }
    cancelAnimationFrame(this.raf)
  }

  requestTiltOnTouch() {
    if (this.tiltPermissionRequested) return
    this.tiltPermissionRequested = true
    DeviceOrientationEvent.requestPermission()
      .then((res) => {
        if (res === "granted") this.addTiltListener()
      })
      .catch(() => {})
  }

  addTiltListener() {
    this.onDeviceOrientation = (e) => {
      // gamma: left-right tilt (-90..90). beta: front-back tilt; ~90 when upright in portrait.
      const gx = (e.gamma ?? 0) / 30
      const by = ((e.beta ?? 90) - 90) / 30 // normalize: 0 when upright
      this.targetX = Math.max(-1, Math.min(1, gx))
      this.targetY = Math.max(-1, Math.min(1, by))
    }
    window.addEventListener("deviceorientation", this.onDeviceOrientation, { passive: true })
  }

  onPointerMove(e) {
    const r = this.element.getBoundingClientRect()
    const x = (e.clientX - (r.left + r.width / 2)) / (r.width / 2)
    const y = (e.clientY - (r.top + r.height / 2)) / (r.height / 2)
    this.targetX = Math.max(-1, Math.min(1, x))
    this.targetY = Math.max(-1, Math.min(1, y))
  }

  animate() {
    this.curX += (this.targetX - this.curX) * this.smoothingValue
    this.curY += (this.targetY - this.curY) * this.smoothingValue

    const x = this.curX * this.maxShiftValue
    const y = this.curY * this.maxShiftValue

    if (this.hasBgTarget) {
      this.bgTarget.style.transform = `translate3d(${x}px, ${y}px, 0)`
    }
    this.raf = requestAnimationFrame(this.animate)
  }
}
